<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import type { ToastAction } from '@/composables/useToast'

interface Props {
  type?: 'success' | 'error' | 'warning' | 'info' | 'loading'
  title?: string
  message: string
  dismissible?: boolean
  showProgress?: boolean
  duration?: number
  createdAt?: number
  actions?: ToastAction[]
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  dismissible: true,
  showProgress: false,
  duration: 5000,
  createdAt: () => Date.now()
})

const emit = defineEmits<{
  (e: 'dismiss'): void
  (e: 'pause'): void
  (e: 'resume'): void
  (e: 'action', action: ToastAction): void
}>()

const isHovered = ref(false)
const progress = ref(100)
let animationFrame: number | null = null

const typeConfig = computed(() => {
  switch (props.type) {
    case 'success':
      return {
        gradient: 'from-emerald-500 to-green-600',
        glow: 'shadow-[0_0_20px_rgba(48,209,88,0.15)]',
        ring: 'ring-emerald-500/20',
        progressBar: 'bg-emerald-500',
        progressTrack: 'bg-emerald-500/10',
        label: 'Succès'
      }
    case 'error':
      return {
        gradient: 'from-red-500 to-rose-600',
        glow: 'shadow-[0_0_20px_rgba(255,59,48,0.15)]',
        ring: 'ring-red-500/20',
        progressBar: 'bg-red-500',
        progressTrack: 'bg-red-500/10',
        label: 'Erreur'
      }
    case 'warning':
      return {
        gradient: 'from-amber-500 to-orange-500',
        glow: 'shadow-[0_0_20px_rgba(255,159,10,0.15)]',
        ring: 'ring-amber-500/20',
        progressBar: 'bg-amber-500',
        progressTrack: 'bg-amber-500/10',
        label: 'Attention'
      }
    case 'info':
      return {
        gradient: 'from-blue-500 to-indigo-500',
        glow: 'shadow-[0_0_20px_rgba(10,132,255,0.15)]',
        ring: 'ring-blue-500/20',
        progressBar: 'bg-blue-500',
        progressTrack: 'bg-blue-500/10',
        label: 'Info'
      }
    case 'loading':
      return {
        gradient: 'from-[#bf9b5e] to-[#a8863f]',
        glow: 'shadow-[0_0_20px_rgba(191,155,94,0.15)]',
        ring: 'ring-[#bf9b5e]/20',
        progressBar: 'bg-[#bf9b5e]',
        progressTrack: 'bg-[#bf9b5e]/10',
        label: 'Chargement'
      }
    default:
      return {
        gradient: 'from-blue-500 to-indigo-500',
        glow: 'shadow-[0_0_20px_rgba(10,132,255,0.15)]',
        ring: 'ring-blue-500/20',
        progressBar: 'bg-blue-500',
        progressTrack: 'bg-blue-500/10',
        label: 'Info'
      }
  }
})

const actionVariantClasses: Record<string, string> = {
  primary: 'bg-neutral-900 text-white hover:bg-neutral-800',
  secondary: 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
  danger: 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
}

function updateProgress() {
  if (!props.showProgress || props.duration <= 0 || isHovered.value) {
    return
  }

  const elapsed = Date.now() - props.createdAt
  const remaining = Math.max(0, props.duration - elapsed)
  progress.value = (remaining / props.duration) * 100

  if (remaining > 0) {
    animationFrame = requestAnimationFrame(updateProgress)
  }
}

function handleMouseEnter() {
  isHovered.value = true
  emit('pause')
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
  }
}

function handleMouseLeave() {
  isHovered.value = false
  emit('resume')
  if (props.showProgress) {
    updateProgress()
  }
}

function handleAction(action: ToastAction) {
  action.onClick()
  emit('action', action)
  emit('dismiss')
}

onMounted(() => {
  if (props.showProgress) {
    updateProgress()
  }
})

onUnmounted(() => {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
  }
})

watch(() => props.createdAt, () => {
  if (props.showProgress) {
    progress.value = 100
    updateProgress()
  }
})
</script>

<template>
  <div
    :class="[
      'toast-card relative flex flex-col overflow-hidden min-w-[340px] max-w-[400px]',
      'rounded-2xl ring-1',
      typeConfig.ring,
      typeConfig.glow
    ]"
    role="alert"
    aria-live="polite"
    aria-atomic="true"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Glass background -->
    <div class="absolute inset-0 bg-white/80 backdrop-blur-2xl" />

    <!-- Subtle gradient accent stripe at top -->
    <div :class="['absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r', typeConfig.gradient]" />

    <!-- Content -->
    <div class="relative flex items-start gap-3.5 p-4">
      <!-- Icon -->
      <div :class="['w-9 h-9 rounded-xl bg-gradient-to-br flex items-center justify-center flex-shrink-0 shadow-sm', typeConfig.gradient]">
        <!-- Spinner for loading -->
        <svg
          v-if="type === 'loading'"
          class="w-[18px] h-[18px] text-white animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-30"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="3"
          />
          <path
            class="opacity-90"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>

        <!-- Success checkmark -->
        <svg v-else-if="type === 'success'" class="w-[18px] h-[18px] text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>

        <!-- Error X -->
        <svg v-else-if="type === 'error'" class="w-[18px] h-[18px] text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>

        <!-- Warning exclamation -->
        <svg v-else-if="type === 'warning'" class="w-[18px] h-[18px] text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>

        <!-- Info -->
        <svg v-else class="w-[18px] h-[18px] text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
        </svg>
      </div>

      <!-- Text content -->
      <div class="flex-1 min-w-0 pt-0.5">
        <p v-if="title" class="text-[13px] font-semibold text-neutral-900 tracking-tight leading-tight">
          {{ title }}
        </p>
        <p
          :class="[
            'text-[13px] leading-relaxed',
            title ? 'text-neutral-500 mt-0.5' : 'text-neutral-700 font-medium'
          ]"
        >
          {{ message }}
        </p>

        <!-- Actions -->
        <div v-if="actions && actions.length > 0" class="flex gap-2 mt-3">
          <button
            v-for="action in actions"
            :key="action.label"
            @click="handleAction(action)"
            :class="[
              'px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200',
              actionVariantClasses[action.variant || 'secondary']
            ]"
          >
            {{ action.label }}
          </button>
        </div>
      </div>

      <!-- Dismiss button -->
      <button
        v-if="dismissible"
        @click="emit('dismiss')"
        class="p-1.5 -mr-1 -mt-0.5 rounded-lg text-neutral-300 hover:text-neutral-500 hover:bg-neutral-100/80 transition-all duration-200 flex-shrink-0"
        aria-label="Fermer la notification"
      >
        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Progress bar -->
    <div
      v-if="showProgress && duration > 0"
      :class="['relative h-[3px] w-full', typeConfig.progressTrack]"
    >
      <div
        :class="['absolute inset-y-0 left-0 rounded-full transition-all duration-100 ease-linear', typeConfig.progressBar]"
        :style="{ width: `${progress}%` }"
      />
    </div>
  </div>
</template>

<style scoped>
.toast-card {
  font-family: var(--font-sans);
}
</style>
